import { BaseService } from './BaseService';
import { API_CONFIG, API_ENDPOINTS } from '../config/api.config';
import { Model } from '../types/api.types';
import { NotFoundError, ApiError } from '../utils/errors';
import type { User } from '../types/api.types';
import type { Assistant } from './AssistantService';
import { currentDate } from '@/shared/utils/dateUtils';

// RecentAssistant extends Assistant with accessed_at field
export interface RecentAssistant extends Assistant {
  accessed_at: string;
}

// Profile photo validation interface
export interface ProfilePhotoValidationResult {
  isValid: boolean;
  error?: string;
}

// Profile photo upload response interface
export interface ProfilePhotoUploadResponse {
  profile_photo_url?: string;
  message: string;
}

export class UserService extends BaseService {
  private readonly baseUrl = API_CONFIG.BACKEND_API_URL;

  async getUser(): Promise<User | null> {
    try {
      const user = await this.get<User>(`${this.baseUrl}/auth/profile/`);
      return user || null;
    } catch (error) {
      console.error('[UserService] Error fetching user:', error);

      // Check if it's a 404 or similar error that should return null
      if (
        error instanceof NotFoundError ||
        (error instanceof Error && error.message.includes('404')) ||
        (error instanceof Error && error.message.includes('Not Found'))
      ) {
        return null;
      }

      throw error;
    }
  }

  async updateUserModel(model: Model): Promise<User> {
    // Backend returns full updated profile, not just success flag
    const response = await this.patch<User>(`${this.baseUrl}/auth/profile/`, {
      default_model: model.id,
    });
    if (!response) {
      throw new Error('Failed to update user model');
    }
    return response;
  }

  async getRecentAssistants(): Promise<RecentAssistant[]> {
    try {
      // The /assistants/recents/ endpoint already returns full assistant data
      const response = await this.get<
        Array<Assistant & { last_accessed?: string }>
      >(API_ENDPOINTS.USERS_RECENTS);

      if (!response || response.length === 0) {
        return [];
      }
      // Debug: Check if backend returns default_model_detail
      // Map the response to RecentAssistant interface
      // The backend returns 'last_accessed' but we need 'accessed_at' for compatibility
      const recentAssistants = response.map(assistant => ({
        ...assistant,
        accessed_at: assistant.last_accessed || currentDate().toISOString(),
        // Explicitly preserve default_model_detail if present
        default_model_detail: assistant.default_model_detail,
      })) as RecentAssistant[];
      return recentAssistants;
    } catch (error) {
      console.error('[UserService] Error fetching recent assistants:', error);
      return [];
    }
  }

  /**
   * Validate profile photo file before upload
   */
  validateProfilePhoto(file: File): ProfilePhotoValidationResult {
    // Check file size (max 5MB)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      return {
        isValid: false,
        error: 'Please upload a JPEG or PNG under 5 MB.',
      };
    }

    // Check file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
    if (!allowedTypes.includes(file.type.toLowerCase())) {
      return {
        isValid: false,
        error: 'Please upload a JPEG or PNG under 5 MB.',
      };
    }

    return { isValid: true };
  }

  /**
   * Upload profile photo
   */
  async uploadProfilePhoto(file: File): Promise<ProfilePhotoUploadResponse> {
    // Validate file first
    const validation = this.validateProfilePhoto(file);
    if (!validation.isValid) {
      throw new ApiError(400, validation.error || 'Invalid file');
    }

    const formData = new FormData();
    formData.append('photo', file);

    try {
      const response = await this.post<User>(
        `${this.baseUrl}/auth/profile/photo/`,
        formData
      );
      return {
        profile_photo_url: response.profile_photo_url,
        message: 'Profile photo updated successfully',
      };
    } catch (error) {
      console.error('[UserService] Error uploading profile photo:', error);
      throw error;
    }
  }

  /**
   * Delete profile photo
   */
  async deleteProfilePhoto(): Promise<{ message: string }> {
    try {
      const response = await this.delete<{ message: string }>(
        `${this.baseUrl}/auth/profile/photo/`
      );

      return response || { message: 'Profile photo deleted successfully' };
    } catch (error) {
      console.error('[UserService] Error deleting profile photo:', error);
      throw error;
    }
  }
}

// Lazy initialization to avoid circular dependency issues
let _userServiceInstance: UserService | null = null;

function getUserServiceInstance(): UserService {
  if (!_userServiceInstance) {
    _userServiceInstance = new UserService();
  }
  return _userServiceInstance;
}

// Export a proxy that lazily initializes the service
export const userService = new Proxy({} as UserService, {
  get(_target, prop) {
    const instance = getUserServiceInstance();
    const value = instance[prop as keyof UserService];
    // Bind methods to the instance
    return typeof value === 'function' ? value.bind(instance) : value;
  },
});
